import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Divider,
} from '@mui/material';
import {
  Mail,
  MessageSquare,
  Phone,
  MapPin,
  Clock,
  Send,
  Github,
  Twitter,
  Linkedin,
} from 'lucide-react';

const ContactUsPage: React.FC = () => {
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Handle form submission logic here
    console.log('Contact form submitted');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Header Section */}
      <Box textAlign="center" mb={6}>
        <Typography
          variant="h2"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2,
          }}
        >
          Contact Us
        </Typography>
        <Typography
          variant="h5"
          color="text.secondary"
          sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}
        >
          Get in touch with the DecentralizedKYC team. We're here to help you with
          decentralized identity verification and answer any questions you may have.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Contact Information */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 4, height: '100%' }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              Get in Touch
            </Typography>
            
            <Box sx={{ mb: 4 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <Mail size={24} style={{ marginRight: 16, color: '#2196F3' }} />
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Email Support
                  </Typography>
                  <Typography color="text.secondary">
                    support@dekyc.io
                  </Typography>
                </Box>
              </Box>
              
              <Box display="flex" alignItems="center" mb={2}>
                <MessageSquare size={24} style={{ marginRight: 16, color: '#2196F3' }} />
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Business Inquiries
                  </Typography>
                  <Typography color="text.secondary">
                    business@dekyc.io
                  </Typography>
                </Box>
              </Box>
              
              <Box display="flex" alignItems="center" mb={2}>
                <Phone size={24} style={{ marginRight: 16, color: '#2196F3' }} />
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Technical Support
                  </Typography>
                  <Typography color="text.secondary">
                    Available 24/7 via Discord
                  </Typography>
                </Box>
              </Box>
              
              <Box display="flex" alignItems="center" mb={2}>
                <Clock size={24} style={{ marginRight: 16, color: '#2196F3' }} />
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Response Time
                  </Typography>
                  <Typography color="text.secondary">
                    Within 24 hours
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />
            
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Follow Us
            </Typography>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Github size={16} />}
                href="#"
                target="_blank"
              >
                GitHub
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Twitter size={16} />}
                href="#"
                target="_blank"
              >
                Twitter
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Linkedin size={16} />}
                href="#"
                target="_blank"
              >
                LinkedIn
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Contact Form */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              Send us a Message
            </Typography>
            
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company/Organization"
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subject"
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message"
                    multiline
                    rows={6}
                    variant="outlined"
                    required
                    placeholder="Please describe your inquiry, technical issue, or business proposal in detail..."
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={<Send size={20} />}
                    sx={{
                      background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                      px: 4,
                      py: 1.5,
                    }}
                  >
                    Send Message
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Additional Information */}
      <Box mt={6}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Card elevation={2}>
              <CardContent sx={{ textAlign: 'center', p: 4 }}>
                <MessageSquare size={48} style={{ color: '#2196F3', marginBottom: 16 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Community Support
                </Typography>
                <Typography color="text.secondary" paragraph>
                  Join our Discord community for real-time support, discussions, and updates from the DecentralizedKYC team and community members.
                </Typography>
                <Button variant="outlined" href="#" target="_blank">
                  Join Discord
                </Button>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card elevation={2}>
              <CardContent sx={{ textAlign: 'center', p: 4 }}>
                <Github size={48} style={{ color: '#2196F3', marginBottom: 16 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Developer Resources
                </Typography>
                <Typography color="text.secondary" paragraph>
                  Access our open-source repositories, documentation, and developer tools to integrate DecentralizedKYC into your applications.
                </Typography>
                <Button variant="outlined" href="#" target="_blank">
                  View Documentation
                </Button>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card elevation={2}>
              <CardContent sx={{ textAlign: 'center', p: 4 }}>
                <MapPin size={48} style={{ color: '#2196F3', marginBottom: 16 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Partnership Opportunities
                </Typography>
                <Typography color="text.secondary" paragraph>
                  Interested in partnering with DecentralizedKYC? Let's discuss how we can work together to advance decentralized identity solutions.
                </Typography>
                <Button variant="outlined" href="mailto:business@dekyc.io">
                  Contact Business Team
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default ContactUsPage;